import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { assertNotNull } from '@/helpers/typing'

import { PodcastEpisodeView } from '@/models/doc.podcast.episode'
import { backLink } from '@/models/backlink'

import DocSummaryPage from '@/app/components/doc/DocSummaryPage.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'PodcastEpisodeSummaryPage',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const wait = useWait()

const episode = ref<Raw<PodcastEpisodeView> | null>(null)

const abortController = ref<AbortController | null>(null)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    const episodeValue = assertNotNull(episode.value)

    episodeValue.initPage(to, from, loadDocument)
    backLink.value.updateMetaTitle(from, episodeValue.title)

    next()
  },
)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component is about to
    // be navigated away from.
    // Has access to `this` component instance.
    const wasLoading = abortController.value !== null
    abortController.value?.abort()

    if (episode.value) {
      // A docView is loaded; adjust it.
      await episode.value.updateReadTimeAndSavePosition()
      backLink.value.updateMetaTitle(from, episode.value.title)
    } else {
      if (wasLoading) {
        // We interrupted loadDocument() before it set episode.value.
        // Restart it.
        await loadDocument(to)
      }
    }
    next()
  },
)

onBeforeMount(async () => {
  wait.start('Loading Doc')
  await loadDocument(route)
})

async function _fetchDocument(
  route: RouteLocationNormalized,
  signal: AbortSignal,
): Promise<PodcastEpisodeView> {
  const url_slug = route.params.url_slug as string

  // TODO: implement offline mode for podcast episodes.
  // if (offline.isOffline) {
  //   await loadBookDetailFromNativeStorage(url_slug)
  // }

  const docData = await backend.getPodcastEpisode(url_slug, signal)
  const userData = await backend.getDocUserData(docData.id, signal)
  const doc = new PodcastEpisodeView(docData, userData)

  // Use author from podcast instead of episode, see #11475
  if (docData.doc_group && docData.doc_group.author) {
    doc.author = docData.doc_group.author
  }

  doc.getPageFromRoute(route)

  return doc
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')

    abortController.value?.abort()
    abortController.value = new AbortController()
    const signal = abortController.value.signal

    const docView = await _fetchDocument(route, signal)
    episode.value = docView

    await docView.initDocument(true)

    if (!docView.isPreviewPage) {
      docView.scrollToTop()
    }
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else if (error.name === 'CanceledError') {
      // The onBeforeRouteUpdate() method cancelled us, but it will restart
      // the load, so we don't need to display an error.
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    abortController.value = null
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DocSummaryPage, {
    doc: episode.value,
    isLoadingDoc: isLoadingDoc.value,
    isErrorLoadingDoc: isErrorLoadingDoc.value,
    class: "layout--podcast"
  }, null, 8, ["doc", "isLoadingDoc", "isErrorLoadingDoc"]))
}
}

})